import type { FunctionComponent } from 'react'
import React, { Fragment } from 'react'
import type { EntityChildrenPayload, EntityReaderPayload } from 'api/data.types'
import { useMoveUser } from './MoveUser.hooks'
import {
  Modal,
  EntityArea,
  ParentEntity,
  BackArrowButton,
  RightArrowButton,
  ChildEntity,
  RightButton,
  BreakLine
} from './MoveUser.styles'
import { useTranslation } from 'i18n'

interface MoveUserProps {
  /**
   * Current entity
   */
  currentEntity: EntityChildrenPayload
  /**
   * reader object
   */
  reader?: EntityReaderPayload
  /**
   * Indicates if should display modal or not
   */
  isOpen: boolean
  /**
   * Apollo client
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  client: any
  /**
   * Modal onClose function
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClose: () => any
}

const MoveUser: FunctionComponent<MoveUserProps> = ({
  currentEntity,
  reader,
  isOpen,
  client,
  onClose
}) => {
  const { t } = useTranslation()
  const {
    containerRef,
    destinationEntity,
    selectedItems,
    selectedParent,
    renderedChildren,
    handleEntityClick,
    resetParentEntities,
    handleRenderedChildren,
    handleSubmit
  } = useMoveUser({ client, currentEntity })

  return (
    <Modal
      show={isOpen}
      canCloseByXBtn
      title={t(`Mover {{userName}} para`, {
        userName: reader.name.toUpperCase()
      })}
      onClose={onClose}
      data-testid={'move-user-modal'}
    >
      <EntityArea ref={containerRef}>
        {Boolean(selectedItems.length) &&
          selectedItems.map(item => {
            if (selectedParent && selectedParent.id !== item.id) {
              return
            }
            return (
              <Fragment key={item.id}>
                <ParentEntity
                  isSelected={destinationEntity === item}
                  hasOpenedChildren={Boolean(
                    renderedChildren.length || item.ancestryDetails
                  )}
                  onClick={() => handleEntityClick(item)}
                >
                  {Boolean(item.ancestryDetails) && (
                    <BackArrowButton
                      data-testid={`back-arrow-${item.id}`}
                      iconName="arrow-left"
                      onClick={() => resetParentEntities()}
                      theme={{ type: 'secondary', size: 'medium' }}
                    />
                  )}
                  {item.name}
                  {!renderedChildren.length && Boolean(item.childrenCount) && (
                    <RightArrowButton
                      data-testid={`right-arrow-${item.id}`}
                      iconName="caret-right"
                      onClick={() => handleRenderedChildren(item)}
                      theme={{ type: 'ghost', size: 'small' }}
                    />
                  )}
                </ParentEntity>
                <BreakLine
                  isBold={Boolean(
                    renderedChildren.length || item.ancestryDetails
                  )}
                />
              </Fragment>
            )
          })}
        {Boolean(renderedChildren.length) &&
          renderedChildren.map(child => {
            return (
              <Fragment key={child.id}>
                <ChildEntity
                  isSelected={destinationEntity === child}
                  onClick={() =>
                    !child.children?.length && handleEntityClick(child)
                  }
                >
                  {child.name}
                  {Boolean(child.childrenCount) && (
                    <RightArrowButton
                      data-testid={`child-right-arrow-${child.id}`}
                      iconName="caret-right"
                      onClick={() => handleRenderedChildren(child)}
                      theme={{ type: 'ghost', size: 'small' }}
                    />
                  )}
                </ChildEntity>
                <BreakLine isBold={false} />
              </Fragment>
            )
          })}
      </EntityArea>
      <RightButton
        data-testid={'submit-button'}
        disabled={!destinationEntity}
        theme={{ type: 'primaryLivros', size: 'large' }}
        text={t('Mover para cá')}
        onClick={() => handleSubmit(reader.id, destinationEntity.id)}
      />
    </Modal>
  )
}

export default MoveUser
